import { Controller } from "@hotwired/stimulus"

// 90mod Command Palette Search — v3.0
// Floating overlay search with keyboard navigation, grouped results, quick actions

export default class extends Controller {
  static targets = ["input", "results", "content", "spinner", "overlay", "palette", "quickActions"]

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.isOpen = false
    this._boundGlobalKeydown = this.handleGlobalKeydown.bind(this)
    document.addEventListener("keydown", this._boundGlobalKeydown)
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundGlobalKeydown)
  }

  // ── Palette lifecycle ──

  handleGlobalKeydown(event) {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault()
      this.isOpen ? this.closePalette() : this.openPalette()
    }
    if (event.key === "Escape" && this.isOpen) {
      event.preventDefault()
      this.closePalette()
    }
  }

  openPalette() {
    this.isOpen = true
    this.overlayTarget.classList.remove("hidden")
    this.paletteTarget.classList.remove("hidden")

    // Slide in from top
    this.paletteTarget.style.transform = "translateY(-100%)"
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.paletteTarget.style.transform = "translateY(0)"
      })
    })

    // Reset state
    this.inputTarget.value = ""
    this.selectedIndex = -1
    this.showQuickActions()

    // Focus input after animation starts
    requestAnimationFrame(() => {
      this.inputTarget.focus()
    })
  }

  closePalette() {
    this.isOpen = false
    this.paletteTarget.style.transform = "translateY(-100%)"
    setTimeout(() => {
      this.overlayTarget.classList.add("hidden")
      this.paletteTarget.classList.add("hidden")
      this.paletteTarget.style.transform = ""
    }, 280)
    this.inputTarget.value = ""
    this.selectedIndex = -1
  }

  // ── Search ──

  query() {
    clearTimeout(this.debounceTimer)
    const q = this.inputTarget.value.trim()

    if (q.length < 2) {
      this.showQuickActions()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchResults(q)
    }, 200)
  }

  async fetchResults(query) {
    this.showSpinner()
    try {
      const response = await fetch(`/search.json?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      if (!response.ok) {
        this.renderError()
        return
      }
      const results = await response.json()
      this.renderResults(results, query)
    } catch (error) {
      console.error("Search error:", error)
      this.renderError()
    } finally {
      this.hideSpinner()
    }
  }

  // ── Keyboard navigation ──

  keydown(event) {
    const items = this.resultsTarget.querySelectorAll("[data-search-item]")

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlightItem(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.highlightItem(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          const link = items[this.selectedIndex].closest("a") || items[this.selectedIndex].querySelector("a")
          if (link) {
            this.closePalette()
            window.location.href = link.href
          }
        } else if (this.inputTarget.value.trim().length >= 2) {
          this.closePalette()
          window.location.href = `/search?q=${encodeURIComponent(this.inputTarget.value.trim())}`
        }
        break
      case "Escape":
        event.preventDefault()
        this.closePalette()
        break
    }
  }

  highlightItem(items) {
    items.forEach((item, i) => {
      const el = item.closest("a") || item
      if (i === this.selectedIndex) {
        el.classList.add("bg-mod-warm-50", "dark:bg-mod-surface")
        el.scrollIntoView({ block: "nearest" })
      } else {
        el.classList.remove("bg-mod-warm-50", "dark:bg-mod-surface")
      }
    })
  }

  // ── Rendering ──

  showQuickActions() {
    // Clear search results
    const searchResults = this.contentTarget.querySelectorAll("[data-search-group]")
    searchResults.forEach(el => el.remove())
    const noResults = this.contentTarget.querySelector("[data-no-results]")
    if (noResults) noResults.remove()
    if (this.hasQuickActionsTarget) {
      this.quickActionsTarget.classList.remove("hidden")
    }
  }

  renderResults(results, query) {
    this.selectedIndex = -1

    // Hide quick actions
    if (this.hasQuickActionsTarget) {
      this.quickActionsTarget.classList.add("hidden")
    }

    // Remove previous search results
    const prev = this.contentTarget.querySelectorAll("[data-search-group]")
    prev.forEach(el => el.remove())
    const prevEmpty = this.contentTarget.querySelector("[data-no-results]")
    if (prevEmpty) prevEmpty.remove()

    if (results.length === 0) {
      const empty = document.createElement("div")
      empty.setAttribute("data-no-results", "")
      empty.innerHTML = `
        <div class="px-5 py-10 text-center">
          <svg class="mx-auto h-10 w-10 text-mod-warm-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
          <p class="text-sm font-medium text-mod-text-muted">No results for "${this.escapeHtml(query)}"</p>
          <p class="text-xs text-mod-warm-400 mt-1.5">Try a different search term</p>
        </div>
      `
      this.contentTarget.appendChild(empty)
      return
    }

    // Group by type
    const grouped = {}
    results.forEach(r => {
      if (!grouped[r.type]) grouped[r.type] = []
      grouped[r.type].push(r)
    })

    const typeConfig = {
      project: { icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"/>', color: "bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" },
      task:    { icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>', color: "bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" },
      ticket:  { icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>', color: "bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" },
      contact: { icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>', color: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" },
      user:    { icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>', color: "bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" }
    }

    const wrapper = document.createElement("div")
    wrapper.setAttribute("data-search-group", "")

    for (const [type, items] of Object.entries(grouped)) {
      const cfg = typeConfig[type.toLowerCase()] || typeConfig.task

      let groupHtml = `
        <div class="py-2">
          <p class="px-5 py-1.5 text-[11px] font-semibold text-mod-warm-500 uppercase tracking-wider">${this.escapeHtml(type)}s · ${items.length}</p>
      `

      items.forEach(item => {
        groupHtml += `
          <a href="${this.escapeHtml(item.path)}" class="flex items-center gap-3 px-5 py-2.5 text-sm hover:bg-mod-warm-50 dark:hover:bg-mod-surface transition-colors" data-search-item>
            <span class="flex items-center justify-center w-8 h-8 rounded-lg ${cfg.color} shrink-0">
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">${cfg.icon}</svg>
            </span>
            <span class="min-w-0 flex-1">
              <span class="block text-sm font-medium text-mod-charcoal dark:text-mod-text truncate">${this.highlightMatch(item.title, query)}</span>
              ${item.subtitle ? `<span class="block text-xs text-mod-text-muted truncate mt-0.5">${this.highlightMatch(item.subtitle, query)}</span>` : ""}
            </span>
            <svg class="h-4 w-4 text-mod-warm-400 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </a>
        `
      })

      groupHtml += "</div>"
      wrapper.innerHTML += groupHtml
    }

    // View all results
    wrapper.innerHTML += `
      <div class="border-t border-mod-border px-5 py-3">
        <a href="/search?q=${encodeURIComponent(query)}" class="flex items-center justify-center gap-2 text-sm font-medium text-mod-copper hover:text-mod-ember transition-colors" data-search-item>
          View all ${results.length} results
          <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
        </a>
      </div>
    `

    this.contentTarget.appendChild(wrapper)
  }

  renderError() {
    if (this.hasQuickActionsTarget) {
      this.quickActionsTarget.classList.add("hidden")
    }
    const prev = this.contentTarget.querySelectorAll("[data-search-group]")
    prev.forEach(el => el.remove())
    const prevEmpty = this.contentTarget.querySelector("[data-no-results]")
    if (prevEmpty) prevEmpty.remove()

    const errEl = document.createElement("div")
    errEl.setAttribute("data-no-results", "")
    errEl.innerHTML = `
      <div class="px-5 py-10 text-center">
        <svg class="mx-auto h-10 w-10 text-red-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
        <p class="text-sm font-medium text-mod-text-muted">Search unavailable</p>
        <p class="text-xs text-mod-warm-400 mt-1.5">Please try again in a moment</p>
      </div>
    `
    this.contentTarget.appendChild(errEl)
  }

  // ── Helpers ──

  showSpinner() {
    if (!this.hasSpinnerTarget) return
    const icon = this.inputTarget.parentElement.querySelector("svg:not([data-search-target])")
    if (icon) icon.classList.add("hidden")
    this.spinnerTarget.classList.remove("hidden")
  }

  hideSpinner() {
    if (!this.hasSpinnerTarget) return
    this.spinnerTarget.classList.add("hidden")
    const icon = this.inputTarget.parentElement.querySelector("svg:not([data-search-target])")
    if (icon) icon.classList.remove("hidden")
  }

  highlightMatch(text, query) {
    const escaped = this.escapeHtml(text)
    const queryEscaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(`(${queryEscaped})`, 'gi')
    return escaped.replace(regex, '<mark class="bg-amber-100/60 dark:bg-amber-900/40 text-mod-charcoal dark:text-mod-text rounded-sm px-0.5">$1</mark>')
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
